import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../../utils/axiosConfig'
import GenderIcon, { MaleIcon, FemaleIcon } from '../../components/common/GenderIcon'

const TournamentParticipation = () => {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const [event, setEvent] = useState(null)
  const [myPlayers, setMyPlayers] = useState([])
  const [selectedPlayers, setSelectedPlayers] = useState([])
  const [registeredPlayers, setRegisteredPlayers] = useState([]) // Full registered player objects with category
  const [participationLimits, setParticipationLimits] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [activeCategory, setActiveCategory] = useState(null) // Selected category object
  const [activeGender, setActiveGender] = useState('Male') // 'Male' or 'Female'
  const [removing, setRemoving] = useState(null) // ID of member being removed
  const [replacingMember, setReplacingMember] = useState(null) // Member being replaced
  const [replacingInProgress, setReplacingInProgress] = useState(false) // Replace API in progress
  const [memberSearch, setMemberSearch] = useState('') // Search filter for members

  useEffect(() => {
    fetchEventDetails()
    fetchMyPlayers()
  }, [eventId])

  const fetchEventDetails = async () => {
    try {
      const response = await api.get(`/user/events/${eventId}`)
      if (response.data.success) {
        const eventData = response.data.data.event
        const registeredPlayersData = response.data.data.registered_players || []
        const limitsData = response.data.data.participation_limits || null

        setEvent(eventData)
        setParticipationLimits(limitsData)
        setRegisteredPlayers(registeredPlayersData)

        // Auto-select first category
        if (eventData.allowed_categories?.length > 0) {
          setActiveCategory(eventData.allowed_categories[0])
          // Auto-select first available gender for this category
          const firstGender = eventData.allowed_categories[0].genders?.[0]
          setActiveGender(firstGender === 'M' ? 'Male' : 'Female')
        }
      }
    } catch (error) {
      console.error('Error fetching event:', error)
      alert('Failed to load event details')
    }
  }

  const fetchMyPlayers = async () => {
    try {
      const response = await api.get('/user/my-players')
      if (response.data.success) {
        setMyPlayers(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching players:', error)
    } finally {
      setLoading(false)
    }
  }

  // Build a prefixed ID from registered player data (to match myPlayers format)
  const buildRegisteredKey = (rp) => {
    const source = rp.member_source || 'player'
    return source + '-' + rp.id
  }

  // Build category+gender string for storage (e.g., "Senior (Male)")
  // Limits are per category+gender, so each tab stores a unique category string
  const getCategoryGenderName = () => {
    if (!activeCategory) return null
    return `${activeCategory.name} (${activeGender})`
  }

  // Check if a player is already registered for the current category+gender tab
  const isRegisteredForCurrentTab = (memberId) => {
    const catGender = getCategoryGenderName()
    if (!catGender) return false
    return registeredPlayers.some(rp => {
      const rpKey = buildRegisteredKey(rp)
      if (rpKey !== memberId) return false
      return rp.registered_category === catGender
    })
  }

  // Get registered player IDs for current category+gender tab
  const getRegisteredIdsForCurrentTab = () => {
    const catGender = getCategoryGenderName()
    if (!catGender) return []
    return registeredPlayers
      .filter(rp => rp.registered_category === catGender)
      .map(rp => buildRegisteredKey(rp))
  }

  // Filter players for the active category + gender tab
  const getFilteredMembers = () => {
    if (!activeCategory || !myPlayers.length) return { players: [], coaches: [], managers: [], officials: [] }

    const genderCode = activeGender === 'Male' ? 'M' : 'F'
    const result = { players: [], coaches: [], managers: [], officials: [] }

    // Normalize a member's gender to 'M' / 'F'
    const memberGender = (member) =>
      (member.gender?.toUpperCase() === 'MALE' || member.gender === 'M') ? 'M' : 'F'

    myPlayers.forEach(member => {
      // Coaches - shown in both gender tabs (a coach can lead either team)
      if (member.member_type === 'coach') {
        result.coaches.push(member)
        return
      }
      // Managers - shown in both gender tabs
      if (member.member_type === 'manager') {
        result.managers.push(member)
        return
      }
      // Officials - shown in both gender tabs
      if (member.member_type === 'official') {
        result.officials.push(member)
        return
      }

      // Players - filter by gender AND age range
      if (member.member_type === 'player' || !member.member_type) {
        const playerGender = memberGender(member)

        // Gender filter
        if (playerGender !== genderCode) return

        // Age filter using category's min_age/max_age
        const playerAge = member.age || 0
        if (activeCategory.min_age && playerAge < activeCategory.min_age) return
        if (activeCategory.max_age && playerAge > activeCategory.max_age) return

        result.players.push(member)
      }
    })

    return result
  }

  // Get current registered counts for the active category+gender (limits are per category+gender)
  const getCurrentCategoryCounts = () => {
    const catGender = getCategoryGenderName()
    if (!catGender) return { players: 0, coaches: 0, managers: 0 }

    const counts = { players: 0, coaches: 0, managers: 0 }
    registeredPlayers.forEach(rp => {
      if (rp.registered_category !== catGender) return
      // Only count player-source members (officials don't count against player/coach/manager limits)
      const source = rp.member_source || 'player'
      if (source !== 'player') return
      const type = rp.member_type || 'player'
      if (type === 'coach') counts.coaches++
      else if (type === 'manager') counts.managers++
      else counts.players++
    })
    return counts
  }

  // Count new selections for current tab
  const getNewSelectionCounts = () => {
    const registeredIds = getRegisteredIdsForCurrentTab()
    const newSelections = selectedPlayers.filter(id => !registeredIds.includes(id))
    const counts = { players: 0, coaches: 0, managers: 0, officials: 0 }

    newSelections.forEach(id => {
      const member = myPlayers.find(p => p.id === id)
      if (member) {
        const type = member.member_type || 'player'
        if (type === 'coach') counts.coaches++
        else if (type === 'manager') counts.managers++
        else if (type === 'official') counts.officials++
        else counts.players++
      }
    })

    return counts
  }

  // Check if adding a member would exceed limits (per category)
  const wouldExceedLimit = (memberId) => {
    if (!participationLimits) return false

    const member = myPlayers.find(p => p.id === memberId)
    if (!member) return false

    const currentCounts = getCurrentCategoryCounts()
    const newCounts = getNewSelectionCounts()
    const memberType = member.member_type || 'player'

    if (memberType === 'coach') {
      return (currentCounts.coaches + newCounts.coaches + 1) > participationLimits.max_coaches
    } else if (memberType === 'manager') {
      return (currentCounts.managers + newCounts.managers + 1) > participationLimits.max_managers
    } else if (memberType === 'player' || !memberType) {
      return (currentCounts.players + newCounts.players + 1) > participationLimits.max_players
    }
    return false
  }

  const handlePlayerToggle = (playerId) => {
    setSelectedPlayers(prev => {
      if (prev.includes(playerId)) {
        return prev.filter(id => id !== playerId)
      } else {
        if (wouldExceedLimit(playerId)) {
          const member = myPlayers.find(p => p.id === playerId)
          const memberType = member?.member_type || 'player'
          const typeName = memberType === 'coach' ? 'coaches' : memberType === 'manager' ? 'managers' : 'players'
          alert(`Cannot select this ${memberType}. You have reached the maximum limit for ${typeName}.`)
          return prev
        }
        return [...prev, playerId]
      }
    })
  }

  // Handle category tab change
  const handleCategoryChange = (category) => {
    setActiveCategory(category)
    setSelectedPlayers([]) // Reset selections when changing category
    // Auto-select first available gender
    const firstGender = category.genders?.[0]
    setActiveGender(firstGender === 'M' ? 'Male' : 'Female')
  }

  // Handle gender tab change
  const handleGenderChange = (gender) => {
    setActiveGender(gender)
    setSelectedPlayers([]) // Reset selections when changing gender
  }

  const handleSubmit = async () => {
    const registeredIds = getRegisteredIdsForCurrentTab()
    const newMembers = selectedPlayers.filter(id => !registeredIds.includes(id))

    if (newMembers.length === 0) {
      alert('Please select at least one new team member to add')
      return
    }

    setSubmitting(true)
    try {
      const categoryName = getCategoryGenderName()

      const registrations = newMembers.map(memberId => {
        const member = myPlayers.find(p => p.id === memberId)
        // Send the original numeric ID, not the prefixed one
        const numericId = member?.original_id || memberId
        // Tell backend whether this is from players or officials table
        const source = member?.member_type === 'official' ? 'official' : 'player'
        return {
          player_id: numericId,
          category: categoryName,
          member_source: source
        }
      })

      const response = await api.post(`/user/events/${eventId}/participate`, {
        registrations,
        registration_notes: ''
      })

      if (response.data.success) {
        alert(`Successfully registered for ${activeCategory?.name} ${activeGender}!`)
        // Refresh data to show updated registrations
        setSelectedPlayers([])
        await fetchEventDetails()
      }
    } catch (error) {
      console.error('Error participating:', error)
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.join('\n')
        alert(`Participation Limits Exceeded:\n\n${errorMessages}`)
      } else {
        alert(error.response?.data?.message || 'Failed to register')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleRemoveParticipant = async (member) => {
    const memberName = member.full_name || member.name || 'this member'
    if (!confirm(`Are you sure you want to remove "${memberName}" from this tournament?`)) return

    const catGender = getCategoryGenderName()
    const regPlayer = registeredPlayers.find(rp => {
      const rpKey = buildRegisteredKey(rp)
      return rpKey === member.id && rp.registered_category === catGender
    })
    if (!regPlayer) return

    const originalId = regPlayer.id
    const memberSource = regPlayer.member_source || 'player'

    setRemoving(member.id)
    try {
      const params = new URLSearchParams({ member_source: memberSource })
      if (catGender) params.append('category', catGender)

      const response = await api.delete(`/user/events/${eventId}/participants/${originalId}?${params}`)
      if (response.data.success) {
        alert(response.data.message || 'Member removed successfully')
        setSelectedPlayers([])
        await fetchEventDetails()
      }
    } catch (error) {
      console.error('Error removing participant:', error)
      alert(error.response?.data?.message || 'Failed to remove member')
    } finally {
      setRemoving(null)
    }
  }

  // Start replace mode - user clicks "Replace" on a registered member
  const startReplace = (member) => {
    setReplacingMember(member)
    setSelectedPlayers([])
  }

  // Cancel replace mode
  const cancelReplace = () => {
    setReplacingMember(null)
  }

  // Execute replacement: remove old member, register new member
  const handleReplaceMember = async (newMember) => {
    if (!replacingMember) return

    const oldName = replacingMember.full_name || 'old member'
    const newName = newMember.full_name || 'new member'
    if (!confirm(`Replace "${oldName}" with "${newName}"?`)) return

    setReplacingInProgress(true)
    try {
      // Step 1: Find the old registration record
      const catGender = getCategoryGenderName()
      const regPlayer = registeredPlayers.find(rp => {
        const rpKey = buildRegisteredKey(rp)
        return rpKey === replacingMember.id && rp.registered_category === catGender
      })
      if (!regPlayer) {
        alert('Could not find registration record')
        return
      }

      const originalId = regPlayer.id
      const memberSource = regPlayer.member_source || 'player'

      // Step 2: Remove old member
      const params = new URLSearchParams({ member_source: memberSource })
      if (catGender) params.append('category', catGender)
      await api.delete(`/user/events/${eventId}/participants/${originalId}?${params}`)

      // Step 3: Register new member
      const newNumericId = newMember.original_id || newMember.id
      const newSource = newMember.member_type === 'official' ? 'official' : 'player'
      await api.post(`/user/events/${eventId}/participate`, {
        registrations: [{
          player_id: newNumericId,
          category: catGender,
          member_source: newSource
        }],
        registration_notes: ''
      })

      alert(`Successfully replaced "${oldName}" with "${newName}"`)
      setReplacingMember(null)
      setSelectedPlayers([])
      await fetchEventDetails()
    } catch (error) {
      console.error('Error replacing member:', error)
      alert(error.response?.data?.message || 'Failed to replace member')
      // Refresh to get correct state even if partial failure
      await fetchEventDetails()
    } finally {
      setReplacingInProgress(false)
    }
  }

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
  }

  if (!event) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Event not found</div>
  }

  const filteredMembers = getFilteredMembers()
  const registeredIds = getRegisteredIdsForCurrentTab()

  // Sort: registered first, then alphabetical. Also filter by search term.
  const sortMembers = (members) => {
    let sorted = [...members].sort((a, b) => {
      const aReg = registeredIds.includes(a.id) ? 0 : 1
      const bReg = registeredIds.includes(b.id) ? 0 : 1
      if (aReg !== bReg) return aReg - bReg
      return (a.full_name || '').localeCompare(b.full_name || '')
    })
    if (memberSearch.trim()) {
      const q = memberSearch.trim().toLowerCase()
      sorted = sorted.filter(m =>
        (m.full_name || '').toLowerCase().includes(q) ||
        (m.player_reg_id || '').toLowerCase().includes(q)
      )
    }
    return sorted
  }

  const sortedPlayers = sortMembers(filteredMembers.players)
  const sortedCoaches = sortMembers(filteredMembers.coaches)
  const sortedManagers = sortMembers(filteredMembers.managers)
  const sortedOfficials = sortMembers(filteredMembers.officials)

  const hasMembers = filteredMembers.players.length > 0 || filteredMembers.coaches.length > 0 || filteredMembers.managers.length > 0 || filteredMembers.officials.length > 0

  // Theme colors
  const primaryColor = '#c00f15'
  const primaryDark = '#a00d12'
  const primaryGradient = `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`

  // Check if a member is a valid replacement candidate
  const isReplacementCandidate = (member) => {
    if (!replacingMember) return false
    const isRegistered = isRegisteredForCurrentTab(member.id)
    if (isRegistered) return false
    if (member.status === 'inactive' || member.status === 'deactivated') return false
    // Must be same member type
    const replacingType = replacingMember.member_type || 'player'
    const memberType = member.member_type || 'player'
    return replacingType === memberType
  }

  // Render a member card (reusable for all member types)
  const renderMemberCard = (member, bgColor, borderColor, labelColor) => {
    const isRegistered = isRegisteredForCurrentTab(member.id)
    const isSelected = selectedPlayers.includes(member.id)
    const isBlocked = member.status === 'inactive'
    const isDeactivated = member.status === 'deactivated'
    const isUnavailable = isBlocked || isDeactivated
    const isDisabled = isRegistered || isUnavailable
    const isBeingRemoved = removing === member.id
    const isBeingReplaced = replacingMember?.id === member.id
    const isCandidate = isReplacementCandidate(member)
    const inReplaceMode = !!replacingMember

    return (
      <div
        key={member.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          padding: '0.85rem 1rem',
          background: isBeingReplaced ? '#fef3c7' : isCandidate ? '#eff6ff' : isRegistered ? '#dcfce7' : isBlocked ? '#fef2f2' : isDeactivated ? '#f5f5f5' : isSelected ? bgColor : 'white',
          border: `2px solid ${isBeingReplaced ? '#f59e0b' : isCandidate ? '#3b82f6' : isRegistered ? '#10b981' : isBlocked ? '#fca5a5' : isDeactivated ? '#d1d5db' : isSelected ? borderColor : '#e5e7eb'}`,
          borderRadius: '8px',
          transition: 'all 0.2s',
          opacity: isBeingRemoved || replacingInProgress ? 0.5 : (inReplaceMode && !isCandidate && !isBeingReplaced && !isRegistered) ? 0.4 : (isUnavailable ? 0.6 : 1)
        }}
      >
        {!isRegistered && !inReplaceMode && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => handlePlayerToggle(member.id)}
            disabled={isDisabled}
            style={{ width: '18px', height: '18px', cursor: isDisabled ? 'not-allowed' : 'pointer' }}
          />
        )}
        {member.photo && (
          <img
            src={member.photo}
            alt={member.full_name}
            style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
            onError={(e) => e.target.style.display = 'none'}
          />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '600', color: isUnavailable ? '#991b1b' : '#1f2937', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            {member.full_name}
            {isBlocked && (
              <span style={{ background: '#fee2e2', color: '#dc2626', padding: '0.125rem 0.5rem', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                <i className="fas fa-ban" style={{ fontSize: '0.6rem' }}></i> Blocked
              </span>
            )}
            {isDeactivated && (
              <span style={{ background: '#f3f4f6', color: '#6b7280', padding: '0.125rem 0.5rem', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                <i className="fas fa-user-slash" style={{ fontSize: '0.6rem' }}></i> Deactivated
              </span>
            )}
            {isRegistered && (
              <span style={{ background: '#10b981', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '500' }}>
                ✓ Registered
              </span>
            )}
            {isBeingReplaced && (
              <span style={{ background: '#f59e0b', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '500' }}>
                <i className="fas fa-exchange-alt" style={{ fontSize: '0.6rem' }}></i> Replacing...
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
            {member.player_reg_id && <span>{member.player_reg_id} • </span>}
            {member.age ? <span>{member.age} yrs • </span> : null}
            {member.position || member.member_type || 'Player'}
          </div>
        </div>
        {/* Registered member: show Replace & Remove buttons (not in replace mode) */}
        {isRegistered && !inReplaceMode && (
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              onClick={() => startReplace(member)}
              disabled={isBeingRemoved}
              style={{
                background: '#dbeafe',
                color: '#2563eb',
                border: '1px solid #93c5fd',
                borderRadius: '6px',
                padding: '0.4rem 0.8rem',
                fontSize: '0.75rem',
                fontWeight: '600',
                cursor: isBeingRemoved ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s'
              }}
            >
              <i className="fas fa-exchange-alt"></i> Replace
            </button>
            <button
              onClick={() => handleRemoveParticipant(member)}
              disabled={isBeingRemoved}
              style={{
                background: '#fee2e2',
                color: '#dc2626',
                border: '1px solid #fca5a5',
                borderRadius: '6px',
                padding: '0.4rem 0.8rem',
                fontSize: '0.75rem',
                fontWeight: '600',
                cursor: isBeingRemoved ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s'
              }}
            >
              {isBeingRemoved ? (
                <><i className="fas fa-spinner fa-spin"></i> Removing...</>
              ) : (
                <><i className="fas fa-times"></i> Remove</>
              )}
            </button>
          </div>
        )}
        {/* Being replaced member: show Cancel button */}
        {isBeingReplaced && (
          <button
            onClick={cancelReplace}
            style={{
              background: '#f3f4f6',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              padding: '0.4rem 0.8rem',
              fontSize: '0.75rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              whiteSpace: 'nowrap'
            }}
          >
            <i className="fas fa-undo"></i> Cancel
          </button>
        )}
        {/* Replacement candidate: show "Select" button */}
        {isCandidate && (
          <button
            onClick={() => handleReplaceMember(member)}
            disabled={replacingInProgress}
            style={{
              background: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '0.4rem 0.8rem',
              fontSize: '0.75rem',
              fontWeight: '600',
              cursor: replacingInProgress ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
              opacity: replacingInProgress ? 0.6 : 1
            }}
          >
            {replacingInProgress ? (
              <><i className="fas fa-spinner fa-spin"></i> Replacing...</>
            ) : (
              <><i className="fas fa-check"></i> Select</>
            )}
          </button>
        )}
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '1.5rem 2rem' }}>
      {/* Page Header */}
      <div style={{
        background: primaryGradient,
        padding: '2rem',
        marginBottom: '1.5rem',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        borderRadius: '12px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h1 style={{ margin: 0, color: 'white', fontSize: '2rem', fontWeight: '700' }}>
              Register for Tournament
            </h1>
            <button
              onClick={() => navigate('/user/events')}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                whiteSpace: 'nowrap'
              }}
            >
              <i className="fas fa-arrow-left"></i>
              Back
            </button>
          </div>
          <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: 'rgba(255,255,255,0.95)', fontWeight: '600' }}>
            {event.event_name || 'Loading...'}
          </h2>
          <p style={{ margin: '0', color: 'rgba(255,255,255,0.85)', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <i className="fas fa-map-marker-alt"></i>
              {event.event_venue || 'N/A'}
            </span>
            <span>•</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <i className="fas fa-calendar"></i>
              {event.event_start_date || 'N/A'} to {event.event_end_date || 'N/A'}
            </span>
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Category Tabs */}
        <div style={{
          background: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          marginBottom: '1rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: '#1f2937', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <i className="fas fa-th-list" style={{ color: primaryColor }}></i>
            Tournament Categories
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {event.allowed_categories?.map((cat, idx) => {
              const isActive = activeCategory?.id === cat.id
              return (
                <button
                  key={idx}
                  onClick={() => handleCategoryChange(cat)}
                  style={{
                    background: isActive ? primaryGradient : '#f3f4f6',
                    color: isActive ? 'white' : '#374151',
                    padding: '0.6rem 1.25rem',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    border: isActive ? 'none' : '1px solid #d1d5db',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: isActive ? '0 2px 4px rgba(192,15,21,0.3)' : 'none'
                  }}
                >
                  {cat.name} ({cat.genders?.map(g => g === 'M' ? 'M' : 'F').join('/')})
                </button>
              )
            })}
          </div>
        </div>

        {/* Gender Sub-Tabs */}
        {activeCategory && (
          <div style={{
            background: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '12px',
            marginBottom: '1rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            display: 'flex',
            gap: '0.5rem'
          }}>
            {activeCategory.genders?.includes('M') && (
              <button
                onClick={() => handleGenderChange('Male')}
                style={{
                  background: activeGender === 'Male' ? '#3b82f6' : '#f3f4f6',
                  color: activeGender === 'Male' ? 'white' : '#374151',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  border: activeGender === 'Male' ? 'none' : '1px solid #d1d5db',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                <MaleIcon style={{ marginRight: '0.4rem' }} /> Male
              </button>
            )}
            {activeCategory.genders?.includes('F') && (
              <button
                onClick={() => handleGenderChange('Female')}
                style={{
                  background: activeGender === 'Female' ? '#ec4899' : '#f3f4f6',
                  color: activeGender === 'Female' ? 'white' : '#374151',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  border: activeGender === 'Female' ? 'none' : '1px solid #d1d5db',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                <FemaleIcon style={{ marginRight: '0.4rem' }} /> Female
              </button>
            )}
            <div style={{ marginLeft: 'auto', fontSize: '0.85rem', color: '#6b7280', display: 'flex', alignItems: 'center' }}>
              {activeCategory.name} - {activeGender}
            </div>
          </div>
        )}

        {/* Participation Limits */}
        {participationLimits && (
          <div style={{
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            border: '2px solid #f59e0b',
            padding: '1.25rem',
            borderRadius: '12px',
            marginBottom: '1rem',
            boxShadow: '0 4px 6px rgba(245,158,11,0.2)'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600' }}>
              <i className="fas fa-info-circle"></i>
              Participation Limits
            </h3>
            {(() => {
              const catCounts = getCurrentCategoryCounts()
              const remPlayers = participationLimits.max_players - catCounts.players
              const remCoaches = participationLimits.max_coaches - catCounts.coaches
              const remManagers = participationLimits.max_managers - catCounts.managers
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                  <div style={{ background: 'white', padding: '0.65rem', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                    <div style={{ fontSize: '0.7rem', color: '#92400e', marginBottom: '0.2rem' }}>Players</div>
                    <div style={{ fontSize: '1.15rem', fontWeight: '600', color: '#1f2937' }}>
                      {catCounts.players} / {participationLimits.max_players}
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#059669', marginTop: '0.2rem', fontWeight: '500' }}>
                      ({participationLimits.playing_members} regular + {participationLimits.extra_members} extra)
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.2rem' }}>
                      {remPlayers} remaining
                    </div>
                  </div>
                  <div style={{ background: 'white', padding: '0.65rem', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                    <div style={{ fontSize: '0.7rem', color: '#92400e', marginBottom: '0.2rem' }}>Coaches</div>
                    <div style={{ fontSize: '1.15rem', fontWeight: '600', color: '#1f2937' }}>
                      {catCounts.coaches} / {participationLimits.max_coaches}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.2rem' }}>
                      {remCoaches} remaining
                    </div>
                  </div>
                  <div style={{ background: 'white', padding: '0.65rem', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                    <div style={{ fontSize: '0.7rem', color: '#92400e', marginBottom: '0.2rem' }}>Managers</div>
                    <div style={{ fontSize: '1.15rem', fontWeight: '600', color: '#1f2937' }}>
                      {catCounts.managers} / {participationLimits.max_managers}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.2rem' }}>
                      {remManagers} remaining
                    </div>
                  </div>
                </div>
              )
            })()}
          </div>
        )}

        {/* Replace Mode Banner */}
        {replacingMember && (
          <div style={{
            background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
            border: '2px solid #3b82f6',
            padding: '1rem 1.25rem',
            borderRadius: '12px',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <i className="fas fa-exchange-alt" style={{ color: '#2563eb', fontSize: '1.2rem' }}></i>
              <div>
                <div style={{ fontWeight: '600', color: '#1e40af', fontSize: '0.95rem' }}>
                  Replace Mode
                </div>
                <div style={{ fontSize: '0.85rem', color: '#1e40af' }}>
                  Replacing <strong>"{replacingMember.full_name}"</strong> — Select a replacement from the list below
                </div>
              </div>
            </div>
            <button
              onClick={cancelReplace}
              style={{
                background: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '0.5rem 1rem',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem'
              }}
            >
              <i className="fas fa-times"></i> Cancel
            </button>
          </div>
        )}

        {/* Members List */}
        {!hasMembers ? (
          <div style={{
            textAlign: 'center',
            padding: '2.5rem',
            background: '#fef2f2',
            borderRadius: '8px',
            color: '#991b1b'
          }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', marginBottom: '0.75rem' }}></i>
            <p style={{ margin: 0, fontSize: '1rem' }}>
              No eligible {activeGender?.toLowerCase()} players found for {activeCategory?.name}.
            </p>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#dc2626' }}>
              Make sure your players' age and gender match the category requirements.
            </p>
          </div>
        ) : (
          <>
            {/* Search Input */}
            <div style={{ marginBottom: '1rem', position: 'relative', background: 'white', borderRadius: '10px', padding: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <i className="fas fa-search" style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 1 }}></i>
              <input
                type="text"
                placeholder="Search by name or registration ID..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.7rem 0.75rem 0.7rem 2.5rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none',
                  background: '#f9fafb',
                  color: '#111827',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => { e.target.style.borderColor = primaryColor; e.target.style.background = '#ffffff' }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#f9fafb' }}
              />
              {memberSearch && (
                <button
                  onClick={() => setMemberSearch('')}
                  style={{
                    position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)',
                    background: '#e5e7eb', border: 'none', borderRadius: '50%', width: '24px', height: '24px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    color: '#6b7280', fontSize: '12px', zIndex: 1
                  }}
                  title="Clear search"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>

            {/* Players Section */}
            {sortedPlayers.length > 0 && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '1.25rem', marginBottom: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h3 style={{
                  margin: '0 0 0.75rem 0', fontSize: '1.1rem', color: '#1f2937',
                  borderBottom: `2px solid ${activeGender === 'Male' ? '#3b82f6' : '#ec4899'}`,
                  paddingBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
                }}>
                  <span style={{
                    background: activeGender === 'Male' ? '#dbeafe' : '#fce7f3',
                    color: activeGender === 'Male' ? '#3b82f6' : '#ec4899',
                    padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.95rem'
                  }}>
                    <GenderIcon gender={activeGender} style={{ marginRight: '0.3rem' }} /> {activeGender} Players ({sortedPlayers.length})
                  </span>
                </h3>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {sortedPlayers.map(player =>
                    renderMemberCard(
                      player,
                      activeGender === 'Male' ? '#dbeafe' : '#fce7f3',
                      activeGender === 'Male' ? '#3b82f6' : '#ec4899',
                      activeGender === 'Male' ? '#3b82f6' : '#ec4899'
                    )
                  )}
                </div>
              </div>
            )}

            {/* Coaches Section */}
            {sortedCoaches.length > 0 && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '1.25rem', marginBottom: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h3 style={{
                  margin: '0 0 0.75rem 0', fontSize: '1.1rem', color: '#1f2937',
                  borderBottom: '2px solid #fbbf24', paddingBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
                }}>
                  <span style={{ background: '#fef3c7', color: '#d97706', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.95rem' }}>
                    <i className="fas fa-user-tie"></i> Coaches ({sortedCoaches.length})
                  </span>
                </h3>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {sortedCoaches.map(member =>
                    renderMemberCard(member, '#fef3c7', '#fbbf24', '#d97706')
                  )}
                </div>
              </div>
            )}

            {/* Managers Section */}
            {sortedManagers.length > 0 && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '1.25rem', marginBottom: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h3 style={{
                  margin: '0 0 0.75rem 0', fontSize: '1.1rem', color: '#1f2937',
                  borderBottom: '2px solid #a78bfa', paddingBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
                }}>
                  <span style={{ background: '#ede9fe', color: '#7c3aed', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.95rem' }}>
                    <i className="fas fa-clipboard-list"></i> Managers ({sortedManagers.length})
                  </span>
                </h3>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {sortedManagers.map(member =>
                    renderMemberCard(member, '#ede9fe', '#a78bfa', '#7c3aed')
                  )}
                </div>
              </div>
            )}

            {/* Officials Section */}
            {sortedOfficials.length > 0 && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '1.25rem', marginBottom: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h3 style={{
                  margin: '0 0 0.75rem 0', fontSize: '1.1rem', color: '#1f2937',
                  borderBottom: '2px solid #16a34a', paddingBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
                }}>
                  <span style={{ background: '#dcfce7', color: '#16a34a', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.95rem' }}>
                    <i className="fas fa-shield-alt"></i> Officials ({sortedOfficials.length})
                  </span>
                </h3>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {sortedOfficials.map(member =>
                    renderMemberCard(member, '#dcfce7', '#16a34a', '#16a34a')
                  )}
                </div>
              </div>
            )}

            {/* Submit Button - hidden in replace mode */}
            {!replacingMember && (
              <div style={{
                position: 'sticky',
                bottom: 0,
                background: 'white',
                padding: '1rem',
                borderTop: '2px solid #e5e7eb',
                borderRadius: '0 0 12px 12px',
                marginTop: '0.5rem',
                boxShadow: '0 -2px 6px rgba(0,0,0,0.05)'
              }}>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '0.95rem', fontWeight: '500' }}>
                      <strong>{selectedPlayers.filter(id => !registeredIds.includes(id)).length}</strong> new member(s) selected
                      <span style={{ color: '#6b7280', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                        for {activeCategory?.name} {activeGender}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || selectedPlayers.filter(id => !registeredIds.includes(id)).length === 0}
                    style={{
                      padding: '0.7rem 1.75rem',
                      background: selectedPlayers.filter(id => !registeredIds.includes(id)).length > 0 ? primaryGradient : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      cursor: selectedPlayers.filter(id => !registeredIds.includes(id)).length > 0 ? 'pointer' : 'not-allowed',
                      opacity: submitting ? 0.6 : 1,
                      boxShadow: selectedPlayers.filter(id => !registeredIds.includes(id)).length > 0 ? '0 4px 12px rgba(192,15,21,0.3)' : 'none'
                    }}
                  >
                    {submitting ? 'Registering...' : `Register for ${activeCategory?.name} ${activeGender}`}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default TournamentParticipation
